import React from 'react';
import {useSelector} from 'react-redux';
import {
  selectCurrentPlayer,
  selectGameResults,
  selectGameType,
  selectNextInitiativePlayerId,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectPlayersConfirmedSetupPhase,
  selectTurnOrder
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {selectIsFieldCutscenePlaying, selectUserId} from "../../../redux/reducers/privateStateReducer/selectors";
import {selectIsParallelSupplyPhase} from "../../../utils/gameState/supply/parallelSupplySelectors";
import {getOwnPlayerId} from "../../../utils/players/getOwnPlayerId";
import {getRankStripe, getTurnStatus, PLAYER_TURN_STATUSES} from "../../../utils/players/getPlayerTurnStatus";
import {getPlayerColorStyle} from "../../../utils/gameUi/getPlayerColorStyle";
import {usePlayersListSideMenu} from "../../../contexts/PlayersListSideMenuContext";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import PlayerFactionIcon from "../../PlayerFactionIcon/PlayerFactionIcon";
import {joinClassNames} from "../../gameUi/joinClassNames";
import './HeaderPlayersRow.css';

/**
 * Игроки в порядке хода; кого нет в порядке хода (подготовка) — в порядке
 * списка, после остальных
 */
const orderPlayersByTurn = (players, turnOrder) => {
  if (!turnOrder || turnOrder.length === 0) {
    return players;
  }
  const byTurn = turnOrder
    .map((playerId) => players.find((player) => player.id === playerId))
    .filter(Boolean);
  return [...byTurn, ...players.filter((player) => !turnOrder.includes(player.id))];
};

/**
 * Строка игроков шапки: кружки-портреты в порядке хода с закладкой цвета,
 * свой — с лентой «вы», вокруг ходящих (в одновременной фазе — всех, кто не
 * закончил) бежит кольцо по рейтинговому образу. Нажатие выдвигает панель
 * списка игроков, повторное — задвигает. На ≥ 1024 px — правая зона шапки,
 * уже — полоса под шапкой (Header решает, где рендерить)
 */
const HeaderPlayersRow = ({className}) => {
  const players = useSelector(selectPlayers) || [];
  const turnOrder = useSelector(selectTurnOrder);
  const gameType = useSelector(selectGameType);
  const userId = useSelector(selectUserId);
  const phase = useSelector(selectPhase);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const gameResults = useSelector(selectGameResults);
  const phaseCompletedPlayers = useSelector(selectPhaseCompletedPlayers);
  const playersConfirmedSetupPhase = useSelector(selectPlayersConfirmedSetupPhase);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  // Кто перехватил инициативу в этом раунде — значок в углу его кружка:
  // кнопка перехвата в футере, а в чужой ход видно только здесь и в списке
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  // Облет захваченных городов (см. scenes/CityCaptureAnimation): ход нового
  // раунда в состоянии уже начался, но не ходит никто — колец «ходит» нет
  const isFieldCutscenePlaying = useSelector(selectIsFieldCutscenePlaying);
  // Строка подсвечена, пока список открыт, даже если поверх лежит рынок
  const {isMenuOpen, toggleMenu} = usePlayersListSideMenu();

  if (players.length === 0) {
    return null;
  }

  const ownPlayerId = getOwnPlayerId(gameType, userId, players);
  // У кого инициатива — первый в порядке хода, как на карточке в списке
  // игроков (PlayerInfo): значок в правом верхнем углу кружка
  const initiativePlayerId = turnOrder && turnOrder.length > 0 ? turnOrder[0] : null;

  const handleClick = (event) => {
    event.stopPropagation();
    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    toggleMenu();
  };

  return (
    <button
      type={'button'}
      className={joinClassNames('header-players-row', isMenuOpen && 'header-players-row_opened', className)}
      onClick={handleClick}
      aria-label={'Список игроков'}
      aria-expanded={isMenuOpen}
    >
      {orderPlayersByTurn(players, turnOrder).map((player) => {
        const turnStatus = (gameResults || isFieldCutscenePlaying) ? PLAYER_TURN_STATUSES.NONE : getTurnStatus(
          player.id,
          currentPlayer,
          phase,
          playersConfirmedSetupPhase || [],
          phaseCompletedPlayers || [],
          isParallelSupplyPhase
        );
        const isOwn = player.id === ownPlayerId;

        return (
          <span
            key={player.id}
            className={joinClassNames(
              'header-players-row_player',
              `header-players-row_player_${turnStatus}`,
              isOwn && 'header-players-row_player_own',
            )}
            style={getPlayerColorStyle(player.color)}
            data-player-id={player.id}
            data-turn-status={turnStatus}
            data-rank-stripe={getRankStripe(player.selectedSkin)}
            title={[
              player.name,
              initiativePlayerId === player.id && 'инициатива',
              nextInitiativePlayerId === player.id && 'перехватил инициативу',
            ].filter(Boolean).join(' — ')}
          >
            {turnStatus === PLAYER_TURN_STATUSES.ACTING && (
              <span className={'header-players-row_ring'} aria-hidden={true}/>
            )}
            <PlayerFactionIcon
              player={player}
              skinId={player.selectedSkin}
              className={'header-players-row_icon'}
              imgClassName={'header-players-row_icon-img'}
            />
            {initiativePlayerId === player.id && (
              <img
                className={'header-players-row_initiative'}
                src={'/img/icons/initiative.png'}
                alt={'Инициатива'}
              />
            )}
            {nextInitiativePlayerId === player.id && (
              <img
                className={'header-players-row_interception'}
                src={'/img/icons/action initiative active.png'}
                alt={'Перехват инициативы'}
              />
            )}
            {/* Закладка цвета игрока под кружком; у своего — лента «вы» */}
            {isOwn
              ? <span className={'header-players-row_own-badge'}>{'вы'}</span>
              : <span className={'header-players-row_color-tab'} aria-hidden={true}/>}
          </span>
        );
      })}
    </button>
  );
};

export default HeaderPlayersRow;
